/**
 * Cliente HTTP da API.
 *
 * Um só lugar decide o que significa uma resposta. No front anterior cada
 * chamada repetia `fetch(...).then(r => r.json())` sem checar `r.ok`: uma
 * resposta 500 virava um objeto de erro tratado como dado, e a tela renderizava
 * `undefined` em vez de dizer que falhou.
 */

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/**
 * Sessão expirada é caso de produto, não de erro: a interface precisa saber
 * distinguir "falhou" de "não está mais autenticado".
 *
 * A mensagem vem do servidor quando ele manda uma. A primeira versão fixava
 * "Sua sessão expirou" para todo 401 — e no `POST /auth/login` isso é
 * simplesmente errado: 401 ali significa senha incorreta. Quem errava a senha
 * lia que a sessão tinha expirado, o que não explica nada e sugere recarregar
 * a página em vez de conferir a senha.
 */
export class UnauthorizedError extends ApiError {
  constructor(message = 'Sua sessão expirou. Entre novamente.') {
    super(401, message);
    this.name = 'UnauthorizedError';
  }
}

export interface ApiOptions extends Omit<RequestInit, 'body'> {
  body?: unknown;
  /** Devolve a resposta crua — necessário para ler ETag e tratar 304. */
  raw?: boolean;
  signal?: AbortSignal;
}

const BASE = '/api';

export async function api<T>(path: string, options: ApiOptions = {}): Promise<T> {
  const { body, raw, headers, ...rest } = options;

  const response = await fetch(BASE + path, {
    ...rest,
    credentials: 'same-origin',
    headers: {
      ...(body !== undefined ? { 'content-type': 'application/json' } : {}),
      ...headers,
    },
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });

  if (raw) return response as T;
  if (response.status === 204) return undefined as T;

  if (response.status === 401) {
    const detalhe = await response.json().then((d: { error?: string }) => d.error).catch(() => undefined);
    throw new UnauthorizedError(detalhe);
  }

  if (!response.ok) {
    // A API responde `{ error }`; se não responder (proxy, HTML de erro), a
    // mensagem genérica é melhor do que despejar o corpo cru na tela.
    const detalhe = await response
      .json()
      .then((data: { error?: string }) => data.error)
      .catch(() => undefined);
    throw new ApiError(response.status, detalhe ?? 'Não foi possível concluir a operação.');
  }

  return (await response.json()) as T;
}

/** Página com cursor — o contrato da Fase 2. */
export interface Page<T> {
  items: T[];
  nextCursor: string | null;
}
